// Background-proof frame clock (ADR 0102). The chroma / ML compositors
// (chromaKey.ts, mlSegment.ts) draw one frame per tick into a <canvas> whose
// captureStream() track REPLACES the published LiveKit camera. ADR 0090 moved
// their render loop off requestAnimationFrame (which the browser PAUSES when the
// tab is backgrounded) to a self-correcting setTimeout pump — but the browser
// ALSO clamps main-thread setTimeout/rAF to a ~1s minimum when the tab is
// backgrounded, so the published canvas still dropped to ~1 fps (a near-frozen
// feed for every fan) the instant the Legend alt-tabbed.
//
// A dedicated Web Worker's timers are NOT throttled when the tab is hidden, and
// its posted messages are still delivered to the (hidden) main thread. So the
// Worker owns the clock (its own setInterval at the target fps) and the main
// thread composites ONE frame per posted tick — the WebGL/2D canvas work + the
// published track stay on the main thread, message handlers run while hidden.
//
// The Worker is built from an INLINE Blob URL (no Vite worker-bundling, no extra
// served file, no CSP worker-src surprises on beta). If a Worker can't be created
// (CSP, no Worker), we fall back to the ADR 0090 self-correcting setTimeout pump —
// main-thread-clamped while hidden, but correct when foregrounded — so compositing
// is never blocked (chroma must never block go-live).

// The worker's source: it is just a clock. On "start" it runs a setInterval at the
// requested interval and posts a tick each time; on "stop" it clears the interval.
const WORKER_SRC = `
var timer = null;
self.onmessage = function (e) {
  var d = e.data || {};
  if (d.type === 'start') {
    if (timer !== null) clearInterval(timer);
    var interval = d.interval > 0 ? d.interval : 16;
    timer = setInterval(function () { self.postMessage(1); }, interval);
  } else if (d.type === 'stop') {
    if (timer !== null) { clearInterval(timer); timer = null; }
  }
};
`;

/** Handle returned by startFrameClock — call stop() to halt ticks and release the worker. */
export interface FrameClock {
  stop(): void;
}

/**
 * Drive `onTick` at ~`fps`, surviving a backgrounded tab.
 *
 * Prefers a dedicated Web Worker clock (not throttled while hidden); falls back to
 * a self-correcting, fps-capped setTimeout pump if a Worker can't be created. The
 * onTick callback is wrapped so a transient throw skips one frame instead of
 * killing the loop for good (the ADR 0090 try/catch loop-body semantics).
 */
export function startFrameClock(fps: number, onTick: () => void): FrameClock {
  const frameMs = 1000 / (fps > 0 ? fps : 30);
  let stopped = false;
  const tick = () => {
    if (stopped) return;
    try {
      onTick();
    } catch {
      /* transient GL/segmentation/canvas error — skip this frame, keep the clock alive */
    }
  };

  // Worker path — background-proof. Guarded: Worker/Blob/URL may be absent (jsdom,
  // some embeds), and createObjectURL / new Worker can throw under CSP.
  try {
    if (
      typeof Worker !== "undefined" &&
      typeof Blob !== "undefined" &&
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL === "function"
    ) {
      const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: "text/javascript" }));
      const worker = new Worker(url);
      worker.onmessage = () => tick();
      worker.postMessage({ type: "start", interval: frameMs });
      return {
        stop() {
          stopped = true;
          try {
            worker.postMessage({ type: "stop" });
          } catch {
            /* ignore */
          }
          try {
            worker.terminate();
          } catch {
            /* ignore */
          }
          try {
            URL.revokeObjectURL(url);
          } catch {
            /* ignore */
          }
        },
      };
    }
  } catch {
    // Worker creation failed (CSP / unavailable) — fall through to the setTimeout pump.
  }

  // Fallback: the ADR 0090 self-correcting setTimeout pump — capped to `fps` and
  // self-correcting (subtracting the work time), but main-thread-clamped to ~1 fps
  // while the tab is backgrounded (the best achievable without a Worker).
  let handle: ReturnType<typeof setTimeout> | 0 = 0;
  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
  const pump = () => {
    if (stopped) return;
    const t0 = now();
    tick();
    if (stopped) return;
    handle = setTimeout(pump, Math.max(0, frameMs - (now() - t0)));
  };
  handle = setTimeout(pump, 0);
  return {
    stop() {
      stopped = true;
      if (handle) clearTimeout(handle);
      handle = 0;
    },
  };
}
